import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Col, Row } from 'react-bootstrap';
import StatisticsCount from './StatisticsCount';

const normalizeSummaryValues = (value) => {
	if (Array.isArray(value)) return value.map((item) => Number(item) || 0);
	return [Number(value) || 0];
};

const normalizeSummaryLabels = (labels, valueCount) => {
	if (!Array.isArray(labels) || valueCount < 2) return null;
	const normalized = labels.map((label) => String(label ?? '').trim());
	if (!normalized.some(Boolean)) return null;
	return normalized;
};

/** 多段对比时用表格式对齐，避免 strip 内 vs 长链换行混乱 */
const COMPARE_TABLE_MIN_PERIODS = 3;

const SummaryCompareTable = ({ salesValues, orderValues, labels, loading, placement, className }) => {
	const isStrip = placement === 'strip';

	return (
		<div
			className={classNames(
				'statistics-chart-summary-stats',
				isStrip ? 'statistics-chart-summary-stats--strip' : 'statistics-chart-summary-stats--footer',
				'statistics-chart-summary-stats--compare-table',
				className,
			)}
			role='group'
			aria-label='图表汇总'
		>
			<div className='statistics-chart-summary-stats__compare-scroll'>
				<table className='statistics-chart-summary-stats__compare-table'>
					<thead>
						<tr>
							<th scope='col' className='statistics-chart-summary-stats__compare-metric-head' aria-hidden />
							{labels.map((label, index) => (
								<th key={`compare-period-${index}`} scope='col' className='statistics-chart-summary-stats__compare-period-head'>
									{label}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						<tr>
							<th scope='row' className='statistics-chart-summary-stats__compare-metric-label'>
								销售总额
							</th>
							{salesValues.map((value, index) => (
								<td key={`compare-sales-${index}`} className='statistics-chart-summary-stats__compare-value'>
									<StatisticsCount value={value} loading={loading} mode='currency' duration={0.45} />
								</td>
							))}
						</tr>
						<tr>
							<th scope='row' className='statistics-chart-summary-stats__compare-metric-label'>
								订单数
							</th>
							{orderValues.map((value, index) => (
								<td key={`compare-count-${index}`} className='statistics-chart-summary-stats__compare-value'>
									<span className='statistics-chart-summary-stats__compare-count'>
										<StatisticsCount value={value} loading={loading} duration={0.45} />
										<span className='statistics-chart-summary-stats__suffix'>单</span>
									</span>
								</td>
							))}
						</tr>
					</tbody>
				</table>
			</div>
		</div>
	);
};

const SummaryValueGroup = ({ values, labels, loading, mode, suffix = null }) => (
	<div
		className={classNames('statistics-chart-summary-stats__value', {
			'statistics-chart-summary-stats__value--labeled': Boolean(labels),
		})}
	>
		{values.map((value, index) => (
			<span key={`summary-${mode ?? 'count'}-${index}`} className='statistics-chart-summary-stats__value-part'>
				{index > 0 ? <span className='statistics-chart-summary-stats__vs'> vs </span> : null}
				<span className='statistics-chart-summary-stats__value-stack'>
					{labels?.[index] ? <span className='statistics-chart-summary-stats__period'>{labels[index]}</span> : null}
					<span className='statistics-chart-summary-stats__value-num'>
						<StatisticsCount value={value} loading={loading} mode={mode} duration={0.45} />
					</span>
				</span>
			</span>
		))}
		{suffix ? <span className='statistics-chart-summary-stats__suffix'>{suffix}</span> : null}
	</div>
);

/** 图表汇总：销售总额 + 订单数；3 段及以上对比用表格对齐，少量对比用 vs 并列 */
const StatisticsChartSummaryStats = ({
	salesTotal = 0,
	orderCount = 0,
	compareLabels = null,
	loading = false,
	placement = 'footer',
	className,
}) => {
	const isStrip = placement === 'strip';
	const salesValues = normalizeSummaryValues(salesTotal);
	const orderValues = normalizeSummaryValues(orderCount);
	const labels = normalizeSummaryLabels(compareLabels, Math.max(salesValues.length, orderValues.length));
	const useCompareTable = Boolean(labels && labels.length >= COMPARE_TABLE_MIN_PERIODS);

	if (useCompareTable) {
		return (
			<SummaryCompareTable
				salesValues={salesValues}
				orderValues={orderValues}
				labels={labels}
				loading={loading}
				placement={placement}
				className={className}
			/>
		);
	}

	if (isStrip) {
		return (
			<div className={classNames('statistics-chart-summary-stats', 'statistics-chart-summary-stats--strip', className)} role='group' aria-label='图表汇总'>
				<div className='statistics-chart-summary-stats__item'>
					<div className='statistics-chart-summary-stats__label'>销售总额</div>
					<SummaryValueGroup values={salesValues} labels={labels} loading={loading} mode='currency' />
				</div>
				<div className='statistics-chart-summary-stats__divider' aria-hidden />
				<div className='statistics-chart-summary-stats__item'>
					<div className='statistics-chart-summary-stats__label'>订单数</div>
					<SummaryValueGroup values={orderValues} labels={labels} loading={loading} suffix='单' />
				</div>
			</div>
		);
	}

	return (
		<footer className={classNames('statistics-chart-summary-stats', 'statistics-chart-summary-stats--footer', className)} aria-label='图表汇总'>
			<Row className='g-0'>
				<Col className='statistics-chart-summary-stats__col statistics-chart-summary-stats__col--start d-flex justify-content-center'>
					<div className='statistics-chart-summary-stats__item text-center'>
						<div className='statistics-chart-summary-stats__label'>销售总额</div>
						<SummaryValueGroup values={salesValues} labels={labels} loading={loading} mode='currency' />
					</div>
				</Col>
				<Col className='statistics-chart-summary-stats__col d-flex justify-content-center'>
					<div className='statistics-chart-summary-stats__item text-center'>
						<div className='statistics-chart-summary-stats__label'>订单数</div>
						<SummaryValueGroup values={orderValues} labels={labels} loading={loading} suffix='单' />
					</div>
				</Col>
			</Row>
		</footer>
	);
};

const summaryValuePropType = PropTypes.oneOfType([PropTypes.number, PropTypes.arrayOf(PropTypes.number)]);

StatisticsChartSummaryStats.propTypes = {
	salesTotal: summaryValuePropType,
	orderCount: summaryValuePropType,
	compareLabels: PropTypes.arrayOf(PropTypes.string),
	loading: PropTypes.bool,
	placement: PropTypes.oneOf(['footer', 'strip']),
	className: PropTypes.string,
};

export default StatisticsChartSummaryStats;
